import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PaywallButton from "../Mui/PaywallButton";
import MarketProLogo from "../../images/MarketProLogo.png";
import "../../../stylesheets/chat_page.css";
import { isMobileBrowser } from "../../utils/user_agent";
import { getInAppModalConfig } from "./inAppModalUtils";

const kindLabel = (kind) => {
  const raw = (kind || "").toString().trim();
  if (!raw) return "New in Incite AI";
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizeActionUrl = (actionUrl) => {
  const raw = (actionUrl || "").toString().trim();
  if (!raw) return "";
  if (/^(https?:\/\/|mailto:|tel:|sms:)/i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
};

export default function InAppNotificationModal({
  notification,
  onClose,
  sidebarOffset = 0,
}) {
  const navigate = useNavigate();
  const isMobile = isMobileBrowser();
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    setOpacity(1);
  }, []);

  const modalConfig = useMemo(
    () => getInAppModalConfig(notification?.metadata || {}),
    [notification?.metadata]
  );
  const actionUrl = normalizeActionUrl(notification?.actionUrl);
  const primaryLabel =
    modalConfig?.buttonLabel?.trim() ||
    (actionUrl ? "View Update" : "Got it");

  const handlePrimaryAction = () => {
    onClose?.();
    if (!actionUrl) return;

    if (/^https?:\/\//i.test(actionUrl)) {
      window.open(actionUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (/^(mailto:|tel:|sms:)/i.test(actionUrl)) {
      window.location.href = actionUrl;
      return;
    }

    navigate(actionUrl);
  };

  return (
    <div
      className="modal modal-paywall"
      style={{
        opacity,
        transition: "opacity 0.35s ease",
        backgroundColor: "rgba(15, 23, 42, 0.28)",
        zIndex: 1600,
      }}
    >
      <div
        className="paywall-modal-content"
        style={{
          marginLeft: !isMobile ? `${sidebarOffset / 2}px` : "unset",
          background: "linear-gradient(180deg, #DBE1F5 0%, #FFFFFF 100%)",
          width: "420px",
          maxWidth: "88%",
          padding: "30px 0",
          boxShadow: "0 28px 90px rgba(15, 23, 42, 0.22)",
        }}
      >
        <Box
          className="base-text"
          sx={{
            fontSize: "15px",
            color: "#2971D7",
            fontWeight: 500,
            position: "absolute",
            top: "20px",
            left: "20px",
            "&:hover": { cursor: "pointer" },
          }}
          onClick={onClose}
        >
          Close
        </Box>

        <Box
          className="base-text"
          sx={{
            gap: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 500,
            fontSize: "36px",
          }}
        >
          <img
            src={MarketProLogo}
            style={{ width: "32px" }}
            className="logo-img"
            alt="Incite AI logo"
          />
          <Box
            className="header-paywall-modal"
            sx={{ fontWeight: 600, color: "#1D1D1F" }}
          >
            Incite AI
          </Box>
        </Box>

        <Box
          className="base-text"
          sx={{
            width: "fit-content",
            margin: "10px auto 0 auto",
            px: 1.25,
            py: 0.5,
            borderRadius: "999px",
            fontWeight: 600,
            fontSize: "11px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#3974CC",
            backgroundColor: "rgba(57, 116, 204, 0.10)",
          }}
        >
          {kindLabel(notification?.kind)}
        </Box>

        <Box
          className="base-text"
          sx={{
            margin: "18px auto 0 auto",
            maxWidth: "84%",
            fontWeight: 600,
            fontSize: "28px",
            lineHeight: "124%",
            color: "#111827",
          }}
        >
          {notification?.title || "Notification"}
        </Box>

        <Box
          className="base-text"
          sx={{
            margin: "18px auto 0 auto",
            maxWidth: "84%",
            maxHeight: "34vh",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            fontWeight: 500,
            fontSize: "15px",
            lineHeight: "165%",
            color: "#334155",
            textAlign: "left",
            px: 1,
          }}
        >
          {notification?.text || ""}
          {notification?.body ? `\n\n${notification.body}` : ""}
        </Box>

        <Box
          className="base-text"
          sx={{
            margin: "18px auto 0 auto",
            maxWidth: "84%",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "150%",
            color: "#64748B",
          }}
        >
          This update is also saved in your Inbox.
        </Box>

        <Box
          sx={{
            width: "280px",
            margin: "22px auto 0 auto",
            maxWidth: "84%",
            minHeight: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PaywallButton
            onClick={handlePrimaryAction}
            sx={{
              backgroundColor: "#3974CC",
              "&:hover": { backgroundColor: "#3974CC" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                fontWeight: 500,
                fontSize: "14px",
                margin: "6px auto",
              }}
            >
              {primaryLabel}
            </Box>
          </PaywallButton>
        </Box>
      </div>
    </div>
  );
}
